import { searchClient } from "@algolia/client-search";
import { debounce } from "perfect-debounce";
import { storeToRefs } from "pinia";
import { useSearchStore } from "~/store/search";
import type AlgoliaProduct from "~~/types/AlgoliaProduct";
import type ProductType from "~~/types/Product";
import type StatamicCollectionRequest from "~~/types/requests/StatamicCollectionRequest";

export function useSearch() {
    const router = useRouter();
    const route = useRoute();
    const config = useRuntimeConfig();

    // Get store data
    const {
        pageMounted,
        result,
        loading,
        selectedState,
        selectedFilters,
        selectedSortBy,
        selectedPageLimit,
        pageNum,
        searchText,
    } = storeToRefs(useSearchStore());
    searchText.value = route.query.search ? String(route.query.search) : searchText.value;

    const sortTypes = [
        { id: `${config.public.ALGOLIA_PREFIX}products_title_asc`, value: "A - Z" },
        { id: `${config.public.ALGOLIA_PREFIX}products_title_desc`, value: "Z - A" },
        { id: `${config.public.ALGOLIA_PREFIX}products_price_asc`, value: "Price: Low to High" },
        { id: `${config.public.ALGOLIA_PREFIX}products_price_desc`, value: "Price: High to Low" },
    ];

    const pageLimits = [
        { id: "20", value: "View 20 per page" },
        { id: "40", value: "View 40 per page" },
        { id: "60", value: "View 60 per page" },
    ];

    // --- Computed ---
    const routeQuery = computed(() => {
        const groupFiltered: Record<string, string[]> = {};
        // Loop through all selected filters. Split the string as key/value, then group them.
        selectedFilters.value.forEach((val: string) => {
            const parts = val.split(":", 2);
            if (parts.length !== 2) return;
            const key = parts[0] as string;
            const value = parts[1] as string;
            if (!groupFiltered[key]) {
                groupFiltered[key] = [];
            }
            groupFiltered[key].push(value);
        });

        const query: Record<string, any> = {};
        // Add search & sort as well
        if (searchText.value) {
            query["search"] = searchText.value;
        }
        if (route.query.sortBy || selectedSortBy.value) {
            query["sortBy"] = selectedSortBy.value;
        }
        if (route.query.limit || selectedPageLimit.value) {
            // Don't show initial default limit in URL. Only add to URL when user changes it.
            if (pageLimits[0]?.id !== selectedPageLimit.value) {
                query["limit"] = selectedPageLimit.value;
            }
        }

        for (const [key, value] of Object.entries(groupFiltered)) {
            query[key] = value.join(","); // Algolia uses ',' as separator
        }

        return query;
    });

    const searchFilters = computed(() => {
        const filters = ["status:published"];

        if (selectedState.value) {
            filters.push("states:" + selectedState.value);
        }

        if (routeQuery.value?.grades) {
            const grades = routeQuery.value.grades.split(",").map((val: string) => {
                return "grades:" + val;
            });
            filters.push(`(${grades.join(" OR ")})`);
        }
        if (routeQuery.value?.subjects) {
            const subjects = routeQuery.value.subjects.split(",").map((val: string) => {
                return "subjects:" + val;
            });
            filters.push(`(${subjects.join(" OR ")})`);
        }
        if (routeQuery.value?.series) {
            const series = routeQuery.value.series.split(",").map((val: string) => {
                return "series:" + val;
            });
            filters.push(`(${series.join(" OR ")})`);
        }

        return filters.join(" AND ");
    });

    const doAlgoliaSearch = computed(() => {
        return !!searchText.value;
    });

    const products = computed(() => {
        if (doAlgoliaSearch.value && result.value?.hits) {
            // Copy object to avoid mutating the original object
            return JSON.parse(JSON.stringify(result.value?.hits ?? []));
        }

        // Statamic was called
        return result.value?.data ?? [];
    });

    const viewPerPage = computed(() => {
        const defaultLimitStr = pageLimits[0]?.id ?? "20";
        let limit = parseInt(defaultLimitStr, 10);
        if (selectedPageLimit.value && pageLimits.map((a) => a.id).includes(selectedPageLimit.value)) {
            limit = parseInt(selectedPageLimit.value);
        }
        return limit;
    });

    // --- Functions ---
    function setResult(newResult: any) {
        result.value = newResult;
    }

    async function handleNavigateTo() {
        // Only navigate when already on the products page to prevent
        // watches in other components (e.g. FeatureProducts) from
        // redirecting the user here on initial load.
        if (!route.path.startsWith("/products")) return;

        let url = `/products`;
        if (selectedState.value) {
            url = `/products/state/${selectedState.value.toLowerCase()}`;
        }

        url += `/page/${pageNum.value}`;

        await navigateTo({
            path: url,
            query: routeQuery.value,
        });
    }

    function setDataFromRoute() {
        // Set page number from URL. Default is 1.
        const arySlug = route.params.slug ?? [];
        if (arySlug.length >= 2 && arySlug[0]?.toLowerCase() == "state") {
            if (arySlug.length >= 4 && arySlug[2]?.toLowerCase() == "page") {
                pageNum.value = arySlug[3] ?? "1";
            }
        } else if (arySlug.length >= 2 && arySlug[0]?.toLowerCase() == "page") {
            pageNum.value = arySlug[1] ?? "1";
        }

        // Set sort by and page limit from URL query
        if (route.query) {
            for (const [key, value] of Object.entries(route.query)) {
                if (key === "sortBy") {
                    selectedSortBy.value = String(value);
                } else if (key === "limit") {
                    selectedPageLimit.value = String(value);
                }
            }
        }
    }

    function setInitialUrlParams() {
        setDataFromRoute();

        router.replace({
            query: routeQuery.value,
        });
    }

    function statamicParams() {
        const params: { [k: string]: any } = {};
        params["filter[status]"] = "published";
        params["limit"] = viewPerPage.value;
        params["page"] = pageNum.value;
        params["sort"] = "-featured,title";
        params["fields"] =
            "c_id,c_slug,coursewave_product_id,isbn,featured" +
            ",title,price_base,price_bulk,qty_25_minimum" +
            ",color_option,price_color_base,price_color_bulk,digital_option,price_digital" + // Need this base on the product type (color, b&w, or digital only)
            ",photo_sm,photo_sm_2x,photo,photo_2x,photo_lg,photo_lg_2x" +
            ",photo_xl,photo_xl_2x,photo_2xl,photo_2xl_2x" +
            ",photo_3xl,photo_3xl_2x,features";
        if (routeQuery.value.sortBy) {
            params["sort"] = routeQuery.value.sortBy;
        }
        if (selectedState.value !== "") {
            params["filter[states:contains]"] = selectedState.value.toLowerCase();
            // sort by state_specific so that state-specific titles show up at the top of the list
            params["sort"] = "state_specific,-featured";
        }
        if (routeQuery.value.search) {
            params["filter[title:contains]"] = routeQuery.value.search;
        }
        if (routeQuery.value?.grades) {
            params["filter[taxonomy:grades]"] = routeQuery.value.grades;
        }
        if (routeQuery.value?.subjects) {
            params["filter[taxonomy:subjects]"] = routeQuery.value.subjects;
        }
        if (routeQuery.value?.series) {
            params["filter[taxonomy:series]"] = routeQuery.value.series;
        }

        return params;
    }

    async function performAlgoliaSearch() {
        const indexName = selectedSortBy.value ? selectedSortBy.value : `${config.public.ALGOLIA_PREFIX}products`;

        try {
            const algoliaClient = searchClient(
                config.public.ALGOLIA_APPLICATION_ID as string,
                config.public.ALGOLIA_API_KEY as string,
            );
            const searchResponse = await algoliaClient.search([
                {
                    indexName,
                    params: {
                        query: routeQuery.value.search ?? selectedState.value ?? null,
                        hitsPerPage: viewPerPage.value,
                        filters: searchFilters.value,
                        page: pageNum.value ? parseInt(pageNum.value) - 1 : 0, // Page start at 0
                    },
                },
            ]);

            result.value = searchResponse.results[0] ?? [];
        } catch (error) {
            console.error("Algolia search error:", error);
            result.value = [];
        }
    }

    async function performStatamicSearch() {
        const params = statamicParams();

        const searchEndpoint = config.public.STATAMIC_API + "/collections/products/entries";
        try {
            const response = await $fetch<StatamicCollectionRequest<ProductType> | null>(searchEndpoint, {
                query: params,
            });
            result.value = response ?? null;
        } catch (err) {
            console.error(err);
            result.value = {};
            return;
        }
    }

    async function fetchCWResources(products: ProductType[] | AlgoliaProduct[]) {
        // Get CW resources for the products
        const resourceIds: number[] = [];
        products.forEach((product: any) => {
            if (product.coursewave_product_id && !resourceIds.includes(product.coursewave_product_id)) {
                resourceIds.push(product.coursewave_product_id);
            }
        });

        if (resourceIds.length === 0) {
            return;
        }
        const requestURL = config.public.STATAMIC_API + `/cw/resource?resource_id=${resourceIds.join(",")}`;
        let cwResources: any;
        try {
            cwResources = await $fetch<any>(requestURL);
        } catch (error: any) {
            const statusCode = error?.status ?? error?.response?.status ?? error?.statusCode;
            if (statusCode === 404 || statusCode >= 500) {
                // Missing/failed CW resource endpoint should not break product rendering.
                console.warn(`CW resource request failed with status ${statusCode}`);
                return;
            }

            console.error("CW resource request error:", error);
            return;
        }

        if (!cwResources || !Array.isArray(cwResources)) {
            return;
        }

        // Map the CW resources back to products
        products.forEach((product: any) => {
            const index = cwResources.findIndex((cwResource: any) => cwResource.id == product.coursewave_product_id);
            if (index !== -1) {
                product.cw_resource = cwResources[index];
            }
        });
    }

    async function fetchResult() {
        try {
            if (loading.value) {
                return;
            }

            loading.value = true;
            if (doAlgoliaSearch.value) {
                // I don't think Algolia search has something like useFetch() to watch for changes. This will do for now.
                await performAlgoliaSearch();
            } else {
                await performStatamicSearch();
            }

            await fetchCWResources(products.value);
        } catch (error) {
            console.error(error);
            loading.value = false;
        } finally {
            loading.value = false;
        }

        return result.value;
    }

    const debounceFetchResult = debounce(async () => {
        pageNum.value = "1"; // Reset page number to 1 since filters have changed
        await fetchResult();
        await handleNavigateTo();
    }, 1000);

    // Watch these data variables. Auto reset page and update result.
    // Guard with pageMounted so initial-load reactive changes (e.g. selectedState
    // being set by fetchUserState) don't trigger a navigation before setup completes.
    watch(selectedPageLimit, (newVal, oldVal) => {
        if (loading.value || !pageMounted.value) return;
        debounceFetchResult();
    });
    watch(selectedSortBy, (newVal, oldVal) => {
        if (loading.value || !pageMounted.value) return;
        debounceFetchResult();
    });
    watch(searchFilters, async (newVal, oldVal) => {
        if (loading.value || !pageMounted.value) return;
        debounceFetchResult();
    });
    watch(searchText, async (newVal, oldVal) => {
        if (loading.value || !pageMounted.value) return;
        debounceFetchResult();
    });

    return {
        result,
        loading,
        pageNum,
        searchText,
        selectedState,
        selectedFilters,
        sortTypes,
        pageLimits,
        selectedSortBy,
        selectedPageLimit,
        searchFilters,
        pageMounted,
        setInitialUrlParams,
        handleNavigateTo,
        fetchResult,
        fetchCWResources,
        debounceFetchResult,
        doAlgoliaSearch,
        products,
        setResult,
        statamicParams,
    };
}
